import { FC, memo } from 'react'
import {
  Backdrop,
  Box,
  Fade,
  IconButton,
  Modal,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

export interface MediaPreviewDialogProps {
  /** Controla la visibilidad del diálogo. */
  open: boolean
  /** Callback al cerrar (ESC, click fuera, botón ✕). */
  onClose: () => void
  /** URL del recurso a mostrar. */
  src: string
  /** Tipo del recurso: `image` o `video`. */
  type: 'image' | 'video'
  /** Texto alternativo para imágenes y título mostrado en la barra inferior. */
  name?: string
}

/**
 * Vista expandida de un recurso multimedia (imagen o video) a sus dimensiones
 * naturales — limitada a `90vw × 90vh` con `object-fit: contain` para preservar
 * proporciones. Reutilizable desde cualquier punto de la app.
 *
 * Cierre por: botón ✕, ESC, o click en el backdrop.
 */
const MediaPreviewDialogBase: FC<MediaPreviewDialogProps> = ({
  open,
  onClose,
  src,
  type,
  name,
}) => {
  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 250,
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.85)' },
        },
      }}
    >
      <Fade in={open} timeout={250} easing={{ enter: 'ease-out', exit: 'ease-in' }}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            outline: 'none',
            p: { xs: 2, md: 4 },
          }}
          onClick={onClose}
        >
          <IconButton
            aria-label="Cerrar"
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: { xs: 12, md: 20 },
              right: { xs: 12, md: 20 },
              zIndex: 2,
              width: { xs: 36, md: 44 },
              height: { xs: 36, md: 44 },
              backgroundColor: 'rgba(255, 255, 255, 0.12)',
              color: '#fff',
              backdropFilter: 'blur(4px)',
              transition: 'background-color 200ms ease, transform 200ms ease',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.22)',
                transform: 'scale(1.05)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box
            // Stop propagation para que el click en el contenido no cierre el modal.
            onClick={(e) => e.stopPropagation()}
            sx={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            {type === 'image' ? (
              <Box
                component="img"
                src={src}
                alt={name || 'preview'}
                sx={{
                  maxWidth: '90vw',
                  maxHeight: name ? '82vh' : '88vh',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: 1,
                  boxShadow: '0 12px 48px rgba(0, 0, 0, 0.6)',
                  backgroundColor: 'rgba(255, 255, 255, 0.04)',
                }}
              />
            ) : (
              <Box
                component="video"
                src={src}
                controls
                autoPlay
                muted
                playsInline
                preload="metadata"
                sx={{
                  maxWidth: '90vw',
                  maxHeight: name ? '82vh' : '88vh',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: 1,
                  boxShadow: '0 12px 48px rgba(0, 0, 0, 0.6)',
                  backgroundColor: '#000',
                }}
              />
            )}

            {name && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 2,
                  py: 1,
                  borderRadius: 999,
                  backgroundColor: 'rgba(255, 255, 255, 0.10)',
                  backdropFilter: 'blur(4px)',
                  color: '#fff',
                  maxWidth: '90vw',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: { xs: '60vw', md: '50vw' },
                  }}
                >
                  {name}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Fade>
    </Modal>
  )
}

const MediaPreviewDialog = memo(MediaPreviewDialogBase)
export default MediaPreviewDialog
